import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

interface QACheckboxCellProps {
	checked: boolean | null | undefined;
	ruleDescription: string;
	onToggle: (checked: boolean) => Promise<void> | void;
}

export function QACheckboxCell({
	checked,
	ruleDescription,
	onToggle,
}: QACheckboxCellProps) {
	// Treat null/undefined (no QA review yet) as unchecked
	const normalizedChecked = checked === true;
	const [saving, setSaving] = useState(false);
	const [optimistic, setOptimistic] = useState(normalizedChecked);

	// Sync optimistic state when prop changes from external source
	if (!saving && optimistic !== normalizedChecked) {
		setOptimistic(normalizedChecked);
	}

	const handleToggle = async () => {
		const newValue = !optimistic;
		setOptimistic(newValue);
		setSaving(true);
		try {
			await onToggle(newValue);
		} catch {
			setOptimistic(normalizedChecked); // revert on error
		} finally {
			setSaving(false);
		}
	};

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<div className="flex items-center justify-center w-full h-full">
					{saving ? (
						<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
					) : (
						<Checkbox
							checked={optimistic}
							onCheckedChange={handleToggle}
							className={cn(
								optimistic &&
									"border-red-500 bg-red-500 text-white data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500",
								!optimistic && checked === null && "border-dashed border-muted-foreground/40",
							)}
						/>
					)}
				</div>
			</TooltipTrigger>
			<TooltipContent side="top" className="max-w-[250px]">
				<p className="text-xs">{ruleDescription}</p>
			</TooltipContent>
		</Tooltip>
	);
}
